import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import CourseCatalog from './pages/CourseCatalog';
import CourseDetail from './pages/CourseDetail';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import StripeReturn from './pages/StripeReturn';
import Dashboard from './pages/Dashboard';
import SessionPicker from './pages/SessionPicker';
import TeamSetup from './pages/TeamSetup';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Eula from './pages/Eula';
import AdminDashboard from './pages/admin/AdminDashboard';
import ManageCourses from './pages/admin/ManageCourses';
import ManageTracks from './pages/admin/ManageTracks';
import ManageSchedules from './pages/admin/ManageSchedules';
import ManageEnrollments from './pages/admin/ManageEnrollments';
import ManageDiscountCodes from './pages/admin/ManageDiscountCodes';
import ManageInstructors from './pages/admin/ManageInstructors';
import ManageSubscriptions from './pages/admin/ManageSubscriptions';
import ApproveSubscription from './pages/admin/ApproveSubscription';
import AddStudent from './pages/admin/AddStudent';
import InstructorDashboard from './pages/instructor/InstructorDashboard';
import './App.css';

const PostAuthRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const redirectPath = localStorage.getItem('postAuthRedirect');
      if (redirectPath) {
        localStorage.removeItem('postAuthRedirect');
        navigate(redirectPath, { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate]);

  return null;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <PostAuthRedirect />
        <Routes>
          <Route path="/" element={<Layout />}>
            {/* Public routes */}
            <Route index element={<Home />} />
            <Route path="courses" element={<CourseCatalog />} />
            <Route path="courses/:courseId" element={<CourseDetail />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="checkout/confirmation" element={<OrderConfirmation />} />
            <Route path="checkout/stripe-return" element={<StripeReturn />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<Eula />} />

            {/* Protected routes */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="choose-session/:subscriptionId"
              element={
                <ProtectedRoute>
                  <SessionPicker />
                </ProtectedRoute>
              }
            />
            <Route
              path="team-setup/:subscriptionId"
              element={
                <ProtectedRoute>
                  <TeamSetup />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/courses"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageCourses />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/tracks"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageTracks />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/schedules"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageSchedules />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/enrollments"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageEnrollments />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/discounts"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageDiscountCodes />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/instructors"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageInstructors />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/subscriptions"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageSubscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/approve"
              element={<ApproveSubscription />}
            />
            <Route
              path="admin/add-student"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AddStudent />
                </ProtectedRoute>
              }
            />

            {/* Instructor routes */}
            <Route
              path="instructor"
              element={
                <ProtectedRoute requiredRole="instructor">
                  <InstructorDashboard />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
